import React, { useCallback, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { ellipsis } from 'polished';
import { Icon } from '../icon/Icon';
import { Button } from '../button/Button';
import { Input, IconWrap } from '../input/Input';
import { Checkbox, Text } from '../checkbox/Checkbox';
import Popper from '../popper/Popper';
import Downshift from 'downshift';
import posed, { PoseGroup } from 'react-pose';

const itemToString = i => (i ? i.name : '');
const toggleItem = (item, arr) => (arr.includes(item) ? arr.filter(x => x !== item) : arr.concat(item));

const Wrapper = styled.div`
  position: relative;
  ${layout}
  display: inline-block;

  ${IconWrap} {
    pointer-events: none;
  }
`;

const StyledInput = styled(Input)`
  cursor: pointer;
`;

const ArrowIcon = styled(Icon).attrs({
  name: 'ARROW_DOWN',
})`
  transition: transform 300ms;
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  color: ${props => (props.isOpen ? themeGet('select.arrow.up.color') : themeGet('select.arrow.color'))};
`;

const Animation = posed.div({
  enter: {
    opacity: 1,
    scale: 1,
    transition: { duration: 150, ease: 'circOut' },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0, ease: 'backOut' },
    delay: 0,
  },
});

const MenuWrapper = styled(Animation)`
  display: flex;
  flex-flow: column;
  width: 100%;
  box-sizing: border-box;
  outline: 0;
  max-height: ${themeGet('select.menu.maxHeight')};
  border: ${themeGet('select.menu.border')};
  border-color: ${themeGet('select.menu.borderColor')};
  border-radius: ${themeGet('select.menu.borderRadius')};
  box-shadow: ${themeGet('select.menu.boxShadow')};
  background-color: ${themeGet('select.menu.backgroundColor')};
  z-index: ${themeGet('select.menu.zIndex')};
  transform-origin: top center;
`;

const Menu = styled.ul`
  width: 100%;
  padding: 8px 0;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
  height: ${themeGet('select.option.height')};
  font-size: ${themeGet('select.option.fontSize')};
  border-top: ${themeGet('select.option.border.top')};
  border-right: ${themeGet('select.option.border.right')};
  border-bottom: ${themeGet('select.option.border.bottom')};
  border-left: ${themeGet('select.option.border.left')};
  border-radius: ${themeGet('select.option.borderRadius')};
  box-shadow: ${themeGet('select.option.boxShadow')};
  color: ${themeGet('select.option.color')};
  background-color: ${themeGet('select.option.backgroundColor')};
  cursor: pointer;

  ${props =>
    props.hovered &&
    css`
      color: ${themeGet('select.option.hover.color')};
      background-color: ${themeGet('select.option.hover.backgroundColor')};
    `}

  ${props =>
    props.selected &&
    css`
      font-weight: 700;
      color: ${themeGet('select.option.selected.color')};
      background-color: ${themeGet('select.option.selected.backgroundColor')};
    `}

  ${props =>
    props.disabled &&
    css`
      color: ${themeGet('select.option.disabled.color')};
      background-color: ${themeGet('select.option.disabled.backgroundColor')};
      cursor: not-allowed;
    `}

  ${Text} {
    ${ellipsis()}
  }
`;

const GroupMenu = styled.ul`
  width: 100%;
  padding: 0;
  margin: 0 0 8px;
  list-style: none;

  &:last-child {
    border-bottom: 0;
  }

  ${Item} {
    padding: 8px 32px;
  }
`;

const GroupTitle = styled.div`
  padding: 8px 16px;
  font-weight: 600;
  font-size: ${themeGet('select.group.title.fontSize')};
  border-top: ${themeGet('select.group.title.border.top')};
  border-right: ${themeGet('select.group.title.border.right')};
  border-bottom: ${themeGet('select.group.title.border.bottom')};
  border-left: ${themeGet('select.group.title.border.left')};
  border-radius: ${themeGet('select.group.title.borderRadius')};
  color: ${themeGet('select.group.title.color')};
  background-color: ${themeGet('select.group.title.backgroundColor')};
`;

const ActionBar = styled.div`
  height: 42px;
  border-top: 1px solid ${themeGet('colors.blacks.9')};
  text-align: right;
`;

const ClearBtn = styled(Button)`
  height: inherit;
  padding: 0 16px;
  cursor: pointer;
`;

const Label = styled.span`
  ${ellipsis()}
`;

export const Select = ({
  width,
  size,
  defaultValue,
  value,
  options,
  placeholder,
  onChange,
  multiple,
  className,
  disabled,
  error,
  disablePortal = false,
  disabledPopper = false,
  inputValue,
  ...rest
}) => {
  const ele = useRef(null);

  const [selectedItems, setSelectedItems] = useState(
    value || (defaultValue !== null ? defaultValue : multiple ? [] : null)
  );

  const onSelectedChange = useCallback(
    option => {
      const result = multiple ? toggleItem(option, selectedItems) : option;

      if (value === undefined) {
        setSelectedItems(result);
      }
      if (typeof onChange === 'function') {
        onChange(result);
      }
    },
    [multiple, value, onChange, selectedItems]
  );

  if (value !== undefined && value !== selectedItems) {
    setSelectedItems(value);
  }

  const onClear = useCallback(() => {
    if (value === undefined) {
      setSelectedItems([]);
    }

    if (typeof onChange === 'function') {
      onChange([]);
    }
  }, [onChange, value]);

  return (
    <Downshift
      itemToString={itemToString}
      initialSelectedItem={defaultValue}
      selectedItem={multiple ? null : value}
      inputValue={
        multiple
          ? selectedItems.length
            ? inputValue || `Select ${selectedItems.length} item`
            : ''
          : selectedItems
          ? inputValue || undefined
          : undefined
      }
      onSelect={onSelectedChange}
      stateReducer={useCallback(
        (state, changes) => {
          if (multiple) {
            switch (changes.type) {
              case Downshift.stateChangeTypes.keyDownEnter:
              case Downshift.stateChangeTypes.clickItem:
                return {
                  ...changes,
                  isOpen: true,
                };
              default:
                return changes;
            }
          } else {
            switch (changes.type) {
              case Downshift.stateChangeTypes.keyDownEnter:
              case Downshift.stateChangeTypes.clickItem:
                const controledValue =
                  value !== undefined
                    ? {
                        selectedItem: value,
                        inputValue: itemToString(value),
                      }
                    : {};

                return { ...changes, ...controledValue };
              default:
                return changes;
            }
          }
        },
        [multiple, value]
      )}
      {...rest}
    >
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        getRootProps,
        isOpen,
        selectedItem,
        highlightedIndex,
        toggleMenu,
      }) => {
        const rootProps = getRootProps();
        return (
          <Wrapper
            width={width}
            {...rootProps}
            ref={node => {
              ele.current = node;
              rootProps.ref(node);
            }}
          >
            <StyledInput
              className={className}
              size={size}
              suffix={<ArrowIcon isOpen={isOpen} />}
              disabled={disabled}
              error={error}
              readOnly
              placeholder={placeholder}
              {...getInputProps()}
              onClick={() => {
                toggleMenu();
              }}
            />
            <Popper
              key="popper"
              disabledPopper={disabledPopper}
              disablePortal={disablePortal}
              keepMounted
              open={isOpen}
              referRef={ele}
              dynamicWidth
            >
              {({ open }) => (
                <PoseGroup>
                  {open && (
                    <MenuWrapper key="menu">
                      <Menu {...getMenuProps()}>
                        {options.map((item, index) =>
                          Array.isArray(item.options) ? (
                            <GroupMenu key={item.name}>
                              <GroupTitle>{item.name}</GroupTitle>
                              {item.options.map(option => (
                                <Item
                                  key={option.name}
                                  {...getItemProps({
                                    item: option,
                                    index: option.value,
                                    hovered: highlightedIndex === option.value,
                                    selected: selectedItem === option,
                                    disabled: option.disabled,
                                  })}
                                >
                                  <Label>{itemToString(option)}</Label>
                                </Item>
                              ))}
                            </GroupMenu>
                          ) : (
                            <Item
                              key={item.name}
                              {...getItemProps({
                                item,
                                index,
                                hovered: highlightedIndex === index,
                                selected: selectedItem === item,
                                disabled: item.disabled,
                              })}
                            >
                              {multiple ? (
                                <Checkbox
                                  onClick={event => event.stopPropagation()}
                                  checked={selectedItems.includes(item)}
                                >
                                  {itemToString(item)}
                                </Checkbox>
                              ) : (
                                <Label>{itemToString(item)}</Label>
                              )}
                            </Item>
                          )
                        )}
                      </Menu>
                      {multiple && (
                        <ActionBar>
                          <ClearBtn variant="link" size="small" onClick={onClear}>
                            Clear
                          </ClearBtn>
                        </ActionBar>
                      )}
                    </MenuWrapper>
                  )}
                </PoseGroup>
              )}
            </Popper>
          </Wrapper>
        );
      }}
    </Downshift>
  );
};

Select.defaultProps = {
  width: '100%',
  defaultValue: null,
};
